import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import httpClient from "../../utils/httpClients";

function EventDetail() {
  const { id } = useParams();
  const location = useLocation();
  const eventFromState = location.state?.event;
  const isArtistPanel = location.pathname.includes("/artist/");
  const eventsListPath = isArtistPanel ? "/artist/events" : "/events";

  const [event, setEvent] = useState(eventFromState || null);
  const [loading, setLoading] = useState(!eventFromState && !!id);
  const [error, setError] = useState(null);

  // Scroll to top when the component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Fetch event by id if not in state (e.g. direct URL or refresh)
  useEffect(() => {
    if (eventFromState) {
      setEvent(eventFromState);
      return;
    }
    if (!id) return;

    const fetchEvent = async () => {
      try {
        setLoading(true);
        const res = await httpClient.get(`/common/event/get-event/${id}`, {
          skipLoader: true,
          skipToast: true,
        });
        if (res.data?.success && res.data?.data) {
          setEvent(res.data.data.event || res.data.data);
        } else {
          setError("Event not found");
        }
      } catch (err) {
        console.error("Error fetching event:", err);
        setError("Failed to load event");
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [id, eventFromState]);

  const formatDate = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getImageUrl = () => {
    if (!event) return null;
    const img = event.images;
    if (Array.isArray(img) && img[0]) {
      return typeof img[0] === "string" ? img[0] : img[0]?.url || img[0];
    }
    return event.imagesUrl?.[0] || event.imageUrl || event.coverUrl;
  };

  if (loading) {
    return (
      <div className="content-inner container-fluid pb-0" id="page_layout">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ minHeight: "40vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="content-inner container-fluid pb-0" id="page_layout">
        <div className="row row--grid">
          <div className="col-12 col-xl-10 offset-xl-1">
            <div className="article">
              <div className="article__content">
                <h1>Event Not Found</h1>
                <p>The event you're looking for is not available.</p>
                <Link
                  to={eventsListPath}
                  className="release__buy mt-3 d-inline-block"
                >
                  Back to Events
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const coverUrl = getImageUrl();
  const displayTime = event.startTime
    ? typeof event.startTime === "string" && event.startTime.includes("T")
      ? formatTime(event.startTime)
      : event.startTime
    : "";

  return (
    <div className="content-inner container-fluid pb-0" id="page_layout">
      <div className="row row--grid">
        <div className="col-12">
          <ul className="breadcrumb">
            <li className="breadcrumb__item">
              <Link to={isArtistPanel ? "/artist/dashboard" : "/"}>Home</Link>
            </li>
            <li className="breadcrumb__item">
              <Link to={eventsListPath}>Events</Link>
            </li>
            <li className="breadcrumb__item breadcrumb__item--active">
              {event.eventName}
            </li>
          </ul>
        </div>
        <div className="col-12">
          <div className="main__title main__title--page">
            <h2 className="card-title text-capitalize mt-3">
              {event.eventName}
            </h2>
          </div>
        </div>
      </div>
      <div className="row row--grid">
        <div className="col-12 col-xl-10 offset-xl-1">
          <div className="article">
            <div className="article__content">
              {coverUrl && (
                <img
                  src={coverUrl}
                  alt={event.eventName}
                  className="article__cover"
                  style={{ height: "30em", objectFit: "fill" }}
                />
              )}
               <div className="article__meta">
                <span className="article__date">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                    <path d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20ZM14.09814,9.63379,13,10.26807V7a1,1,0,0,0-2,0v5a1.00025,1.00025,0,0,0,1.5.86621l2.59814-1.5a1.00016,1.00016,0,1,0-1-1.73242Z" />
                  </svg>{" "}
                  {formatDate(event.eventDate)}
                  {displayTime && ` • ${displayTime}`}
                </span>
                {event.venue && (
                  <span className="article__category d-block mt-2">
                    <strong>Venue:</strong> {event.venue}
                  </span>
                )}
              </div>
              {event.description ? (
                typeof event.description === "string" &&
                (event.description.includes("<") ||
                  event.description.includes("&lt;")) ? (
                  <div
                    dangerouslySetInnerHTML={{ __html: event.description }}
                  />
                ) : (
                  <p style={{ whiteSpace: "pre-wrap" }}>{event.description}</p>
                )
              ) : (
                <p>No description available.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default EventDetail;
